/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * VersionChecker — detects when a newer frontend build has been deployed
 * while this tab stayed open, and offers a one-click reload.
 *
 * Background: index.html is served `no-store`, but the hashed JS chunks are
 * cached `immutable`. A tab left open across a deploy keeps running its old
 * bundle — and once-navigated lazy chunks may 404 or silently serve stale
 * UI. This polls index.html, compares the entry bundle hash, and surfaces a
 * non-intrusive "Reload" banner so the user updates on their own terms
 * (rather than losing unsaved work to a forced reload).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const BUNDLE_RE = /assets\/(index-[A-Za-z0-9_-]+\.js)/;

// The entry bundle's hashed filename, read from this document's own scripts.
const getRunningBundle = () => {
  for (const s of document.querySelectorAll('script[src]')) {
    const m = (s.getAttribute('src') || '').match(BUNDLE_RE);
    if (m) return m[1];
  }
  return null;
};

const VersionChecker = () => {
  const [updateReady, setUpdateReady] = useState(false);
  const runningRef = useRef(getRunningBundle());
  const location = useLocation();

  const check = useCallback(async () => {
    if (!runningRef.current || updateReady) return;
    try {
      const res = await fetch(`/?_v=${Date.now()}`, { cache: 'no-store' });
      const html = await res.text();
      const m = html.match(BUNDLE_RE);
      if (m && m[1] !== runningRef.current) setUpdateReady(true);
    } catch { /* offline / transient — try again next tick */ }
  }, [updateReady]);

  // Check on every route change, and every 5 minutes while idle.
  useEffect(() => { check(); }, [location.pathname, check]);
  useEffect(() => {
    const id = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [check]);

  if (!updateReady) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, background: '#1D1D44', color: '#fff', borderRadius: 10,
      padding: '10px 16px', boxShadow: '0 8px 28px rgba(0,0,0,0.28)',
      display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.88rem',
      maxWidth: 'calc(100vw - 32px)',
    }}>
      <i className="bi bi-arrow-clockwise" style={{ color: '#9B30FF' }} />
      <span>A new version of VowsEra is available.</span>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: '#9B30FF', color: '#fff', border: 'none', borderRadius: 6,
          padding: '5px 14px', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
        }}
      >
        Reload
      </button>
    </div>
  );
};

export default VersionChecker;
